import { cn } from "@/lib/utils";

interface RatingStarsProps {
  value: number | null;
  /** Gir klikkbare stjerner (radiogruppe) når satt. */
  onChange?: (value: number) => void;
  size?: "sm" | "lg";
  className?: string;
}

const STAR_PATH = "M12 3.5 l2.7 5.5 l6 .9 l-4.3 4.2 l1 6 l-5.4 -2.8 l-5.4 2.9 l1 -6 l-4.3 -4.2 l6 -.9 Z";

function Star({ filled, className }: { filled: boolean; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d={STAR_PATH} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round" />
    </svg>
  );
}

/** Vurdering 1–5. Visning avrunder snittet; redigering er en tastaturstyrt radiogruppe. */
export function RatingStars({ value, onChange, size = "sm", className }: RatingStarsProps) {
  const rounded = value == null ? 0 : Math.round(value);
  const starClass = size === "lg" ? "h-7 w-7" : "h-3.5 w-3.5";

  if (!onChange) {
    return (
      <span
        role="img"
        aria-label={value == null ? "Ikke vurdert" : `${value.toLocaleString("nb-NO")} av 5`}
        className={cn("inline-flex gap-px text-primary", className)}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} filled={n <= rounded} className={cn(starClass, n > rounded && "text-border")} />
        ))}
      </span>
    );
  }

  return (
    <div role="radiogroup" aria-label="Vurdering" className={cn("inline-flex gap-1 text-primary", className)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={rounded === n}
          aria-label={`${n} av 5`}
          tabIndex={rounded === n || (rounded === 0 && n === 1) ? 0 : -1}
          onClick={() => onChange(n)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight" || e.key === "ArrowUp") onChange(Math.min(5, (rounded || 0) + 1));
            if (e.key === "ArrowLeft" || e.key === "ArrowDown") onChange(Math.max(1, (rounded || 2) - 1));
          }}
          className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Star filled={n <= rounded} className={cn(starClass, n > rounded && "text-border")} />
        </button>
      ))}
    </div>
  );
}
